// Roles that act as an author and get a Stripe trial. Any other
// non-empty `?role=` value (an opaque marketing / invite string — e.g.
// `beta-reader`, `editor`) is treated as "joining someone else's
// workspace" and skips the trial. Add new author-style roles here as
// they're introduced.
export const TRIAL_ROLES = new Set<string>(['coauthor']);

export function shouldSkipTrial(role: string | null | undefined): boolean {
  if (!role) return false;
  return !TRIAL_ROLES.has(role.trim().toLowerCase());
}

// Maps an invite-role slug (as minted by DW3's `roleToInviteSlug`) to the i18n
// key for the human label shown in the invited-signup banner. Slugs not listed
// here fall back to generic "invited to collaborate" copy. `coauthor` is
// deliberately absent — it's an author starting their own trial, not an invite.
const INVITE_ROLE_LABEL_KEYS: Record<string, string> = {
  editor: 'invite_role_editor',
  'beta-reader': 'invite_role_beta_reader',
  reviewer: 'invite_role_reviewer',
  reader: 'invite_role_reader',
};

/** The i18n key for an invite role's label, or undefined for unknown roles. */
export function inviteRoleLabelKey(role: string | null | undefined): string | undefined {
  if (!role) return undefined;
  return INVITE_ROLE_LABEL_KEYS[role.trim().toLowerCase()];
}

/**
 * Whether the `?role=` slug represents a collaborator joining someone else's
 * workspace (beta reader, editor, …) rather than an author signing up. These
 * users get a free, no-trial account and the invited-signup banner.
 */
export function isInviteRole(role: string | null | undefined): boolean {
  return Boolean(role) && shouldSkipTrial(role);
}
